import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRecaptcha } from "../hooks/useRecaptcha";
import { toast } from "sonner";
import { Mail, Lock, ArrowLeft, ShieldCheck } from "lucide-react";

/** POST /api/auth/forgot-password → user requests a reset link */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { getToken: getCaptchaToken } = useRecaptcha();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const captcha_token = await getCaptchaToken("forgot_password");
      await api.post("/auth/forgot-password", { email, captcha_token });
      setSent(true);
    } catch (err) {
      // Endpoint always 200s to prevent enumeration — but surface parse errors
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <ResetShell title="Reset your password">
      {sent ? (
        <div data-testid="forgot-sent">
          <div className="grid place-items-center h-14 w-14 rounded-full bg-primary/10 text-primary">
            <Mail className="w-6 h-6" />
          </div>
          <h2 className="font-display text-2xl mt-4">Check your inbox</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            If an account exists for <span className="font-semibold">{email}</span>, we've sent a link that expires in 30 minutes.
          </p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary" data-testid="forgot-back-login">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Enter the email you used to sign up. We'll send a magic link so you can set a new password.
          </p>
          <form onSubmit={submit} className="space-y-4 mt-5">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email</span>
              <input value={email} type="email" required onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email"
                className="mt-1 w-full rounded-xl bg-white border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors" />
            </label>
            <button type="submit" disabled={busy || !email} className="btn-primary w-full justify-center" data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary" data-testid="forgot-back-login">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </>
      )}
    </ResetShell>
  );
}

/** POST /api/auth/reset-password → user sets a new password using the emailed token */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [check, setCheck] = useState(null); // null | {email} | {error}
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setCheck({ error: "Missing reset token." }); return; }
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setCheck({ email: r.data.email }))
      .catch((e) => setCheck({ error: formatError(e) }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      await refresh?.();
      toast.success("Password updated. You're signed in.");
      nav("/dashboard", { replace: true });
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <ResetShell title="Choose a new password">
      {!check ? (
        <p className="text-sm text-muted-foreground">Checking your reset link…</p>
      ) : check.error ? (
        <div data-testid="reset-error">
          <p className="text-sm text-destructive">{check.error}</p>
          <Link to="/forgot-password" className="mt-4 btn-primary inline-flex" data-testid="reset-restart">
            Request a new link
          </Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Setting a new password for <span className="font-semibold">{check.email}</span>.
          </p>
          <form onSubmit={submit} className="space-y-4 mt-5">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">New password</span>
              <input value={password} type="password" required onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password"
                className="mt-1 w-full rounded-xl bg-white border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Confirm password</span>
              <input value={confirm} type="password" required onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                className="mt-1 w-full rounded-xl bg-white border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors" />
            </label>
            <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="reset-submit">
              {busy ? "Updating…" : "Update password & sign in"}
            </button>
            <p className="text-[11px] text-muted-foreground flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
              After you save, we'll sign you in and drop you on your dashboard.
            </p>
          </form>
        </>
      )}
    </ResetShell>
  );
}

function ResetShell({ title, children }) {
  // Auth-specific violet→pink gradient (per brand guidance) — kept inline
  // so we don't override the app-wide `.grad-band` used elsewhere.
  const AUTH_GRAD = "linear-gradient(103deg, #1C39CE 0%, #6F28BC 58%, #D31787 100%)";
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div
        className="hidden md:flex flex-col justify-between p-12 text-white"
        style={{ backgroundImage: AUTH_GRAD }}
      >
        <Link to="/" className="flex items-center gap-2" data-testid="reset-brand" aria-label="CPG Institute by BrandVault — Home">
          <img
            src="/brand/cpg-logo.png"
            alt="CPG Institute — by BrandVault 360"
            className="block w-auto h-20 md:h-24 lg:h-28 select-none"
            draggable={false}
          />
        </Link>
        <div className="max-w-md">
          <p className="font-display text-4xl leading-tight">Locked out? We'll get you back in.</p>
          <p className="mt-6 text-sm text-white/80">Passwordless magic link. No support ticket needed.</p>
        </div>
        <p className="text-xs uppercase tracking-widest text-white/70">Warren Ezra · CPG Institute</p>
      </div>
      <div className="flex items-center justify-center p-8 md:p-14">
        <div className="w-full max-w-sm">
          {/* Mobile-only brand — mirrors Auth.jsx so the reset flow feels
              like the same product on phones. Dark-text variant since
              the mobile panel is white. */}
          <img
            src="/brand/cpg-logo-dark.png"
            alt="CPG Institute — by BrandVault 360"
            className="md:hidden block h-16 w-auto mb-6 select-none"
            draggable={false}
          />
          <div className="grid place-items-center h-10 w-10 rounded-full bg-primary/10 text-primary mb-4">
            <Lock className="w-4 h-4" />
          </div>
          <h1 className="font-display text-4xl mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}
